import { EmptyListContent } from '@euler/app/components/list/EmptyListContent';
import { InspectionResultsView } from '@euler/app/flows/report/components/inspection/InspectionResultsView';
import {
  DefectiveGroupInfoMap,
  InspectionResultCellModel,
  InspectionResultFormatter,
  InspectionResultGroup,
  ItemModel,
  toMediaObject,
} from '@euler/app/flows/report/functions';
import {
  getDefectiveLevel,
  getDefectiveLevelTokenByValue,
} from '@euler/functions/getDefectiveLevel';
import {
  isSimpleNumericInspectionResultDataType,
  isSimpleStringInspectionResultDataType,
} from '@euler/functions/inspectionResultDataTypeHelpers';
import {
  useInventory,
  useInventoryLookup,
} from '@euler/functions/useInventory';
import { MediaObject } from '@euler/model';
import { DefectiveLevel } from '@euler/model/DefectiveLevel';
import {
  AbnormalLevel,
  OptionValueType,
  PendingIssueType,
} from '@euler/model/enum';
import { DefectiveLevel as DefectiveLevelString } from '@euler/model/enum/DefectiveLevel';
import {
  InspectionReportItemType,
  MediaSourceType,
  PendingIssue,
} from '@euler/model/report';
import { memo, useMemo } from 'react';
import {
  Platform,
  RefreshControl,
  ScrollView,
  ScrollViewProps,
} from 'react-native';

const kDefectiveStringToNumMap: {
  [p in DefectiveLevelString]: DefectiveLevel;
} = {
  normal: DefectiveLevel.Fine,
  slight: DefectiveLevel.Defective,
  warning: DefectiveLevel.Warning,
  urgent: DefectiveLevel.Urgent,
};

function defectiveLevelFromString(s: DefectiveLevelString) {
  return kDefectiveStringToNumMap[s];
}

export const PendingIssueListView = memo(
  ({
    pendingIssues,
    loading,
    refreshing,
    error,
    bottomSheet,
    onRefresh,
    ...props
  }: {
    pendingIssues: PendingIssue[];
    loading?: boolean;
    refreshing?: boolean;
    error?: Error;
    onRefresh?: () => void;
    bottomSheet?: boolean;
  } & ScrollViewProps) => {
    const [inventory] = useInventory();
    const inventoryQuery = useInventoryLookup(inventory);

    const cells = useMemo(() => {
      const res: InspectionResultCellModel[] = [];
      const groups: InspectionResultGroup[] = [];

      const sectionFor = (
        defectiveLevel: DefectiveLevel,
        title: string,
        type: PendingIssueType | undefined,
      ) => {
        let group = groups.find(x => x.defectiveLevel === defectiveLevel);
        if (!group) {
          group = {
            defectiveLevel,
            sections: [],
          };
          groups.push(group);
        }
        let section = group.sections.find(x => x.title === title);
        if (!section) {
          section = {
            title,
            tag: type === PendingIssueType.Fault ? '故障检修' : undefined,
            items: [],
            defectiveLevel: DefectiveLevel.Fine, // default defective level
          };
          group.sections.push(section);
        }
        return section;
      };

      for (const issue of pendingIssues) {
        const defectiveLevel = defectiveLevelFromString(issue.defectiveLevel);
        const section = sectionFor(defectiveLevel, issue.name, issue.type);
        section.defectiveLevel = defectiveLevel;

        for (const detail of issue.details) {
          const inspectionResult = JSON.parse(
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            detail.inspectionResult || '{"vt":"string","v":""}',
          ) as CompactInspectionResult;
          const site = detail.siteId
            ? inventoryQuery.getSiteById(detail.siteId)
            : undefined;
          const item = detail.itemId
            ? site?.checkItems.find(x => x.id === detail.itemId)
            : undefined;

          let abnormalDescription: string | undefined = undefined;
          if (item != null) {
            const abnormalOption =
              item?.options.find(
                x =>
                  x.label === detail.title &&
                  x.abnormalLevel === detail.abnormalLevel &&
                  x.severityLevel === detail.severityLevel,
              ) ??
              item?.options.find(
                x =>
                  x.abnormalLevel === detail.abnormalLevel &&
                  x.severityLevel === detail.severityLevel,
              );

            const abnormalOptionWithDescription = item?.options.find(
              x => x.abnormalLevel !== AbnormalLevel.Fine && x.description,
            );
            abnormalDescription =
              abnormalOption?.description ??
              abnormalOptionWithDescription?.description ??
              undefined;
          }

          section.items.push({
            type: detail.isCustomIssue
              ? InspectionReportItemType.CustomIssue
              : InspectionReportItemType.Normal,
            inspectionType: detail.inspectionType,
            siteId: detail.siteId,
            siteCode: detail.siteCode,
            siteName: detail.siteName,
            siteInfluenceFactor: 0,
            siteItemId: detail.itemId,
            siteItemName: detail.itemName,
            siteItemInfluenceFactor: 0,
            defectiveClass: detail.defectiveClass,
            abnormalLevel: detail.abnormalLevel,
            severityLevel: detail.severityLevel,
            maintenanceAdvice: issue.name,
            resultDataType: inspectionResult.vt,
            resultDataStringValue: isSimpleStringInspectionResultDataType(
              inspectionResult.vt,
            )
              ? inspectionResult.v || detail.title
              : undefined,
            resultDataNumberValue: isSimpleNumericInspectionResultDataType(
              inspectionResult.vt,
            )
              ? inspectionResult.v
              : undefined,
            resultDataUnit: inspectionResult.vu,
            abnormalResultLabelFormat: inspectionResult.of,
            abnormalResultLabel: detail.title,
            resultRemark: detail.remark,
            abnormalDescription,
            data: [],
            medias: detail.medias.map(media => ({
              id: media.cid,
              type: media.type,
              sourceType: MediaSourceType.Inspection,
              category: '',
              url: media.url,
              coverUrl: media.coverUrl,
              dateRef: new Date(),
            })),
          });
        }
      }

      let groupIndex = 0;
      for (const group of groups) {
        const groupToken = getDefectiveLevelTokenByValue(group.defectiveLevel);
        const groupInfo = DefectiveGroupInfoMap[group.defectiveLevel];
        res.push({
          type: 'group-header',
          id: `group/${groupToken}/header`,
          title: groupInfo.title,
          description: groupInfo.description,
          count: group.sections.length,
          defectiveLevel: group.defectiveLevel,
          index: groupIndex++,
        });
        group.sections.forEach((section, index) => {
          const medias: MediaObject[] = [];
          const items: ItemModel[] = [];

          for (const item of section.items) {
            medias.push(...item.medias.map(x => toMediaObject(x)));
            const name =
              InspectionResultFormatter.getInspectedItemDisplayName(item);
            const [label, valueRange] =
              InspectionResultFormatter.formatLabel(item);
            const defectiveLevel = getDefectiveLevel(
              item.abnormalLevel,
              item.severityLevel,
            );

            let description: string | undefined = undefined;
            if (item.type === InspectionReportItemType.CustomIssue) {
              description = item.resultRemark;
            } else {
              description =
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                item.abnormalDescription ||
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                item.resultRemark;
            }

            items.push({
              type: item.type,
              name,
              label: label ?? '',
              valueRange: valueRange ?? undefined,
              defectiveLevel,
              defectiveClass: item.defectiveClass,
              description: description?.trim() ?? '暂无相关故障影响说明',
            });
          }

          // section cell
          res.push({
            type: 'section',
            id: `group/${groupToken}/section/${index}`,
            index,
            title: section.title,
            tag: section.tag,
            defectiveLevel: section.defectiveLevel,
            medias,
            items,
            productConfirmations: undefined,
          });
        });
      }

      return res;
    }, [inventoryQuery, pendingIssues]);

    if (!pendingIssues?.length) {
      return (
        <ScrollView
          css={`
            flex: 1;
            background-color: #fff;
          `}
          contentContainerStyle={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          refreshControl={
            Platform.OS === 'web' || !onRefresh ? undefined : (
              <RefreshControl
                refreshing={refreshing ?? false}
                onRefresh={onRefresh}
              />
            )
          }
        >
          <EmptyListContent
            loading={loading}
            error={error}
            onRetry={onRefresh}
          />
        </ScrollView>
      );
    }

    return (
      <InspectionResultsView
        bottomSheet={bottomSheet}
        cells={cells}
        refreshControl={
          Platform.OS === 'web' || !onRefresh ? undefined : (
            <RefreshControl
              refreshing={refreshing ?? false}
              onRefresh={onRefresh}
            />
          )
        }
        {...props}
      />
    );
  },
);

type CompactInspectionResult = {
  vt: OptionValueType;
  v: any;
  vu?: string;
  of?: string;
  ol?: string;
  ou?: string;
  oli?: boolean;
  oui?: boolean;
};
